import type { FormEvent } from 'react'
import { useState, useEffect } from 'react'
import {
  XMarkIcon,
  CheckIcon,
  CurrencyDollarIcon,
  PlusIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline'
import type { Application, Currency, ExtractedPosting, SalaryPeriod, SalaryRange, WorkModeType } from '../types'
import { SUPPORTED_CURRENCIES } from '../utils/currency'
import { PRESET_STAGE_OPTIONS } from '../services/stagePresets'

const PERIOD_OPTIONS: { value: SalaryPeriod; label: string }[] = [
  { value: 'hourly', label: '/hour' },
  { value: 'monthly', label: '/month' },
  { value: 'yearly', label: '/year' },
]

type StageItem = {
  id: string
  name: string
  completed: boolean
}

const createId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)

type ConfirmData = Partial<Application> & {
  targetAmount?: number | null
  targetPeriod?: SalaryPeriod
  targetCurrency?: Currency
}

type Props = {
  isOpen: boolean
  extraction: ExtractedPosting | null
  onConfirm: (data: ConfirmData) => void
  onCancel: () => void
  isLoading?: boolean
}

export const EditExtractedDataModal = ({
  isOpen,
  extraction,
  onConfirm,
  onCancel,
  isLoading,
}: Props) => {
  const [company, setCompany] = useState('')
  const [positionTitle, setPositionTitle] = useState('')
  const [techStack, setTechStack] = useState('')
  const [contractType, setContractType] = useState('')
  const [benefits, setBenefits] = useState('')
  const [salaryMin, setSalaryMin] = useState('')
  const [salaryMax, setSalaryMax] = useState('')
  const [salaryCurrency, setSalaryCurrency] = useState<Currency>('PLN')
  const [salaryPeriod, setSalaryPeriod] = useState<SalaryPeriod>('monthly')
  const [targetAmount, setTargetAmount] = useState('')
  const [targetPeriod, setTargetPeriod] = useState<SalaryPeriod>('monthly')
  const [targetCurrency, setTargetCurrency] = useState<Currency>('PLN')
  const [stages, setStages] = useState<StageItem[]>([])
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [workMode, setWorkMode] = useState<WorkModeType>(null)
  const [officeDays, setOfficeDays] = useState<number | null>(null)

  // Update form when extraction data changes
  useEffect(() => {
    if (extraction) {
      setCompany(extraction.company || '')
      setPositionTitle(extraction.positionTitle || '')
      setTechStack((extraction.techStack || []).join(', '))
      setContractType(extraction.contractType || '')
      setBenefits(extraction.benefits || '')
      setSalaryMin(extraction.salaryRange?.min?.toString() || '')
      setSalaryMax(extraction.salaryRange?.max?.toString() || '')
      setSalaryCurrency((extraction.salaryRange?.currency as Currency) || 'PLN')
      setSalaryPeriod(extraction.salaryRange?.period ?? extraction.salaryPeriod ?? 'monthly')
      setTargetAmount('')
      setTargetPeriod('monthly')
      setTargetCurrency('PLN')
      // Convert extracted stages to editable format
      setStages(
        (extraction.stages || []).map((name) => ({
          id: createId(),
          name,
          completed: false,
        }))
      )
      setWorkMode(extraction.workMode ?? null)
      setOfficeDays(extraction.officeDays ?? null)
    }
  }, [extraction, isOpen])

  if (!isOpen || !extraction) {
    return null
  }

  const addStage = () => {
    setStages((prev) => [
      ...prev,
      { id: createId(), name: PRESET_STAGE_OPTIONS[0], completed: false },
    ])
  }

  const removeStage = (id: string) => {
    setStages((prev) => prev.filter((s) => s.id !== id))
  }

  const updateStage = (id: string, name: string) => {
    setStages((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)))
  }

  const reorderStages = (from: number, to: number) => {
    if (from === to) return
    setStages((prev) => {
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
  }

  const handleDragStart = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    setDraggedIndex(index)
    event.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (_index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (draggedIndex !== null) {
      reorderStages(draggedIndex, index)
    }
    setDraggedIndex(null)
  }

  const handleDragEnd = () => setDraggedIndex(null)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    const newTechStack = techStack
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)

    const salaryRange: SalaryRange =
      salaryMin || salaryMax
        ? {
            min: salaryMin ? Number(salaryMin) : 0,
            max: salaryMax ? Number(salaryMax) : 0,
            currency: salaryCurrency,
            period: salaryPeriod,
          }
        : null

    const targetNum = targetAmount ? Number(targetAmount) : null

    // Clean up stages
    const cleanedStages = stages
      .map((s) => ({ ...s, name: s.name.trim() }))
      .filter((s) => s.name.length > 0)

    onConfirm({
      company: company || 'N/D',
      positionTitle: positionTitle || null,
      techStack: newTechStack,
      contractType: contractType || null,
      benefits: benefits || null,
      workMode,
      officeDays: workMode === 'hybrid' ? officeDays : null,
      salaryRange,
      targetAmount: Number.isFinite(targetNum) ? targetNum : null,
      targetPeriod,
      targetCurrency,
      processStages: cleanedStages,
    })
  }

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur px-4 !mt-0">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-app-line bg-app-surface shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 py-4 border-b border-app-line bg-app-surface/90 backdrop-blur-md">
          <div>
            <h2 className="text-lg font-bold text-app-ink font-display">
              {extraction?.company ? 'Review extracted data' : 'Add application manually'}
            </h2>
            <p className="text-sm text-app-muted">
              {extraction?.company ? 'Verify and edit before adding' : 'Enter job application details'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="rounded-lg p-2 text-app-muted hover:bg-app-subtle hover:text-app-ink transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form className="p-6 space-y-5" onSubmit={handleSubmit}>
          {/* Company */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Company
            </label>
            <input
              type="text"
              required
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Enter company name"
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none"
            />
          </div>

          {/* Position Title */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Position Title
            </label>
            <input
              type="text"
              value={positionTitle}
              onChange={(e) => setPositionTitle(e.target.value)}
              placeholder="e.g., Senior Frontend Developer, Full Stack Engineer"
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none"
            />
          </div>

          {/* Tech Stack */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Tech Stack
            </label>
            <input
              type="text"
              value={techStack}
              onChange={(e) => setTechStack(e.target.value)}
              placeholder="React, TypeScript, Node.js"
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none"
            />
            <p className="text-xs text-app-muted mt-1">Separate with commas</p>
          </div>

          {/* Salary Range from posting */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Salary range (from posting)
            </label>
            <div className="grid grid-cols-4 gap-2">
              <input
                type="number"
                value={salaryMin}
                onChange={(e) => setSalaryMin(e.target.value)}
                placeholder="Min"
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none"
              />
              <input
                type="number"
                value={salaryMax}
                onChange={(e) => setSalaryMax(e.target.value)}
                placeholder="Max"
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none"
              />
              <select
                value={salaryCurrency}
                onChange={(e) => setSalaryCurrency(e.target.value as Currency)}
                className="w-full rounded-lg border border-app-line bg-app-surface px-2 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
              <select
                value={salaryPeriod ?? 'monthly'}
                onChange={(e) => setSalaryPeriod(e.target.value as SalaryPeriod)}
                className="w-full rounded-lg border border-app-line bg-app-surface px-2 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Target Salary - highlighted */}
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CurrencyDollarIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <label className="text-xs font-medium uppercase text-emerald-700 dark:text-emerald-400">
                Your target salary
              </label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="number"
                min="0"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                placeholder="Amount"
                className="w-full rounded-lg border border-emerald-300 dark:border-emerald-900/50 bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-emerald-500 focus:outline-none"
              />
              <select
                value={targetCurrency}
                onChange={(e) => setTargetCurrency(e.target.value as Currency)}
                className="w-full rounded-lg border border-emerald-300 dark:border-emerald-900/50 bg-app-surface px-2 py-2.5 text-sm text-app-ink focus:border-emerald-500 focus:outline-none"
              >
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
              <select
                value={targetPeriod ?? 'monthly'}
                onChange={(e) => setTargetPeriod(e.target.value as SalaryPeriod)}
                className="w-full rounded-lg border border-emerald-300 dark:border-emerald-900/50 bg-app-surface px-2 py-2.5 text-sm text-app-ink focus:border-emerald-500 focus:outline-none"
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-xs text-app-muted mt-1.5">
              Optional – helps track if offers match your target
            </p>
          </div>

          {/* Process Stages */}
          <div className="rounded-xl border border-sky-200 dark:border-sky-900/50 bg-sky-50 dark:bg-sky-950/20 p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-medium uppercase text-sky-700 dark:text-sky-400">
                Interview Stages
              </label>
              <button
                type="button"
                onClick={addStage}
                className="flex items-center gap-1 text-xs text-app-accent hover:text-app-accent-hover transition-colors"
              >
                <PlusIcon className="w-3.5 h-3.5" />
                Add stage
              </button>
            </div>

            {stages.length === 0 ? (
              <p className="text-sm text-app-muted text-center py-3">
                No stages detected. Add stages to track your progress.
              </p>
            ) : (
              <div className="space-y-2">
                {stages.map((stage, idx) => (
                  <div
                    key={stage.id}
                    draggable
                    onDragStart={(e) => handleDragStart(idx, e)}
                    onDragOver={(e) => handleDragOver(idx, e)}
                    onDrop={(e) => handleDrop(idx, e)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 ${
                      draggedIndex === idx
                        ? 'border-sky-500 bg-sky-100 dark:bg-app-subtle'
                        : 'border-app-line bg-app-surface'
                    }`}
                  >
                    <Bars3Icon className="h-4 w-4 text-app-muted cursor-grab shrink-0" />
                    <span className="text-xs text-app-muted w-5">{idx + 1}.</span>
                    <select
                      value={stage.name}
                      onChange={(e) => updateStage(stage.id, e.target.value)}
                      className="flex-1 min-w-0 border-0 bg-transparent px-1 py-1 text-sm text-app-ink focus:outline-none focus:ring-1 focus:ring-app-accent rounded-lg"
                    >
                      {PRESET_STAGE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                      {!PRESET_STAGE_OPTIONS.includes(stage.name) && (
                        <option value={stage.name}>{stage.name}</option>
                      )}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeStage(stage.id)}
                      className="shrink-0 p-1 text-app-muted hover:bg-app-subtle hover:text-rose-600 rounded-lg"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-app-muted mt-2">
              Drag to reorder • These stages will be used to track your interview progress
            </p>
          </div>

          {/* Contract Type */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Contract Type
            </label>
            <input
              type="text"
              value={contractType}
              onChange={(e) => setContractType(e.target.value)}
              placeholder="UoP, B2B, UZ, etc."
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none"
            />
          </div>

          {/* Work Mode */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Work Mode
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={workMode ?? ''}
                onChange={(e) => {
                  const value = e.target.value as WorkModeType
                  setWorkMode(value || null)
                  if (value !== 'hybrid') setOfficeDays(null)
                }}
                className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              >
                <option value="">Not specified</option>
                <option value="onsite">On-site</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
              {workMode === 'hybrid' && (
                <select
                  value={officeDays ?? ''}
                  onChange={(e) => setOfficeDays(e.target.value ? Number(e.target.value) : null)}
                  className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink focus:border-app-accent focus:outline-none"
                >
                  <option value="">Days in office</option>
                  <option value="1">1 day in office</option>
                  <option value="2">2 days in office</option>
                  <option value="3">3 days in office</option>
                  <option value="4">4 days in office</option>
                  <option value="5">5 days in office</option>
                </select>
              )}
            </div>
          </div>

          {/* Benefits */}
          <div>
            <label className="text-xs font-medium uppercase text-app-muted mb-1.5 block">
              Benefits
            </label>
            <textarea
              value={benefits}
              onChange={(e) => setBenefits(e.target.value)}
              placeholder="e.g., 26 days paid leave, remote work..."
              rows={2}
              className="w-full rounded-lg border border-app-line bg-app-surface px-3 py-2.5 text-sm text-app-ink placeholder:text-app-muted focus:border-app-accent focus:outline-none resize-none"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 bg-app-primary px-4 py-3 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-60 transition-colors rounded-lg"
            >
              {isLoading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Add Application
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="border border-app-line bg-app-surface px-6 py-3 text-sm font-semibold text-app-ink hover:bg-app-subtle transition-colors rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

